import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { BarChart3, Download, Sparkles, TrendingUp } from 'lucide-react';

const metrics = [
  { title: 'Time to hire', value: '24 days', change: '-6 days vs last quarter' },
  { title: 'Interview to offer', value: '32%', change: '+4pp vs benchmark' },
  { title: 'Returner hires', value: '18', change: '+22% QoQ' },
  { title: 'Offer acceptance', value: '89%', change: '+9pp QoQ' },
];

const channelPerformance = [
  { channel: 'Saved searches', hires: 14, conversion: '7.6%' },
  { channel: 'Talent pools', hires: 11, conversion: '6.1%' },
  { channel: 'Returner campaigns', hires: 9, conversion: '8.9%' },
  { channel: 'External referrals', hires: 6, conversion: '4.3%' },
];

export function EmployerAnalytics() {
  return (
    <main className="min-h-screen bg-[#080a19] pb-24" aria-labelledby="employer-analytics-title">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 id="employer-analytics-title" className="text-3xl font-semibold text-white sm:text-4xl">Hiring performance</h1>
            <p className="text-sm text-gray-400">
              Monitor funnel health, returner outcomes, and opportunities to improve equity.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Download className="mr-2 h-4 w-4" aria-hidden />
              Export CSV
            </Button>
            <Button className="bg-gold-gradient text-black hover:brightness-110">
              <Sparkles className="mr-2 h-4 w-4" aria-hidden />
              Generate AI insight
            </Button>
          </div>
        </div>

        <section className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => (
            <Card key={metric.title} className="border-white/10 bg-white/[0.03]">
              <CardContent className="space-y-3 p-6">
                <p className="text-sm text-gray-400">{metric.title}</p>
                <p className="text-3xl font-semibold text-white">{metric.value}</p>
                <p className="text-xs uppercase tracking-[0.3em] text-[#f7ef8a]">{metric.change}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <BarChart3 className="h-5 w-5 text-[#ba9036]" aria-hidden />
                Funnel summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 p-6 pt-0">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Views → Applications</p>
                  <p className="mt-3 text-3xl font-semibold text-white">6.8%</p>
                  <p className="text-sm text-gray-400">
                    Up 0.9pp since launching inclusive job-spec wording with Lighthouse templates.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Returner conversion</p>
                  <p className="mt-3 text-3xl font-semibold text-white">11.2%</p>
                  <p className="text-sm text-gray-400">
                    Candidates finishing the AI interview practice have 3.2× higher conversion to offer.
                  </p>
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Equity check</p>
                <p className="mt-3 text-sm text-gray-300">
                  Offer acceptance for caregivers is down 6pp this month. Lighthouse recommends reviewing scheduling flexibility
                  and interview follow-up messaging.
                </p>
                <Button variant="ghost" className="mt-3 text-[#f7ef8a] hover:text-[#f7ef8a]">
                  View recommendations
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <TrendingUp className="h-5 w-5 text-[#ba9036]" aria-hidden />
                Channel performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6 pt-0">
              {channelPerformance.map((entry) => (
                <div key={entry.channel} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-white">{entry.channel}</p>
                    <Badge className="border-white/10 bg-white/[0.07] text-gray-200">{entry.conversion} conversion</Badge>
                  </div>
                  <p className="mt-2 text-sm text-gray-300">{entry.hires} hires in the last 30 days.</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
}
